/**
 * Shared recovery-request wire schemas.
 *
 * The recovery REQUEST flow is the user-facing surface for
 * "I lost my passkey; please help me get back in." It's the
 * SUBMIT side of recovery — distinct from the existing
 * `recovery` feature which handles completion (validate code +
 * enroll new passkey after an admin/peer has approved).
 *
 * Endpoint:
 *   POST /api/ceremony/v1/recovery/request
 *
 * Request body matches the credrecovery.submitRequestBody shape
 * in the backend. The trust matrix and the peer-resolution
 * gates live server-side; the frontend just submits the user's
 * choice and renders whatever the server decides.
 */

import { z } from "zod";

// A navigation hint only; the form and server validate the submitted address.
export const recoveryRequestSearchSchema = z.object({
  email: z.string().trim().max(256).optional().catch(undefined),
});


// AttestationPathChoice — the user's pick at submit time. Only
// meaningful for untrusted-context requests; the backend ignores
// the choice for trusted-context submits and resolves the path
// to "auto" internally.
export const attestationPathChoiceSchema = z.enum(["admin", "peers"]);
export type AttestationPathChoice = z.infer<typeof attestationPathChoiceSchema>;

// Submit shape. PeerEmail1/2 are only required when the choice
// is "peers"; the discriminated rule lives in the form's
// superRefine so RHF + Zod can produce per-field errors.
export const submitRecoveryRequestSchema = z
  .object({
    email: z.string().min(1, "Enter your email").email("Enter a valid email address"),
    reason: z.string().max(500, "Reason must be 500 characters or fewer").optional(),
    attestation_path_choice: attestationPathChoiceSchema,
    peer_email_1: z.string().optional(),
    peer_email_2: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.attestation_path_choice !== "peers") return;
    // Peer emails are required + email-shaped when the user
    // picked the peer path. Collected here (not on the field
    // schemas above) so the requirements are conditional on
    // the user's choice.
    for (const slot of ["peer_email_1", "peer_email_2"] as const) {
      const v = value[slot];
      if (!v || v.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a colleague's email",
          path: [slot],
        });
        continue;
      }
      // Use a permissive structural check — the backend does the
      // authoritative resolution. We just want to catch obvious
      // typos before the round-trip.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a valid email address",
          path: [slot],
        });
      }
    }
    // Cheap structural duplicate check. Server is authoritative
    // (does case-insensitive + trimmed comparison) but catching
    // the exact-match case here saves a round-trip.
    if (
      value.peer_email_1 &&
      value.peer_email_2 &&
      value.peer_email_1.trim().toLowerCase() ===
        value.peer_email_2.trim().toLowerCase()
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Pick a different colleague",
        path: ["peer_email_2"],
      });
    }
  });
export type SubmitRecoveryRequestInput = z.infer<typeof submitRecoveryRequestSchema>;

// Server response on a successful submit. The backend never
// confirms whether the email matched a real account
// (anti-enumeration), so this shape is intentionally minimal.
export const submitRecoveryRequestResponseSchema = z.object({
  submitted: z.literal(true),
  message: z.string(),
});
export type SubmitRecoveryRequestResponse = z.infer<
  typeof submitRecoveryRequestResponseSchema
>;

// 422 response from the backend when one or both named peers
// failed resolution. Carries per-slot machine-readable codes so
// the frontend can render localized inline errors.
export const peerResolutionErrorSchema = z.object({
  error: z.literal("peer_resolution_failed"),
  message: z.string(),
  peers: z.object({
    peer_1_error: z.string(),
    peer_2_error: z.string(),
  }),
});
export type PeerResolutionError = z.infer<typeof peerResolutionErrorSchema>;

// Stable codes the backend emits — keep in lockstep with
// credrecovery/named_peers.go ResolutionError values. The
// frontend maps these to display copy in the page component.
export type PeerErrorCode =
  | ""
  | "not_found"
  | "not_active"
  | "not_federated"
  | "no_active_credential"
  | "self"
  | "duplicate"
  | "lookup_unavailable";

// peerErrorCopy translates a stable code to display copy. The
// empty-string case ("this slot was fine; the OTHER slot
// failed") returns null so the form renders no error.
export function peerErrorCopy(code: PeerErrorCode): string | null {
  switch (code) {
    case "":
      return null;
    case "not_found":
      return "We don't recognize this email in your organization. Check the spelling.";
    case "not_active":
      return "This account isn't currently active.";
    case "not_federated":
      return "This person isn't part of your organization's identity provider yet.";
    case "no_active_credential":
      return "This person doesn't have a passkey set up — they can't approve a recovery request.";
    case "self":
      return "You can't name yourself as a recovery peer.";
    case "duplicate":
      return "Pick a different colleague.";
    case "lookup_unavailable":
      return "We couldn't reach the directory. Please try again in a moment.";
    default:
      return "This colleague couldn't be verified. Try a different email.";
  }
}
